import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  createProjectCheckoutSession, 
  createProjectPayPalOrder, 
  PROJECT_FEES 
} from '../../services/projectPaymentService';

const ProjectPaymentModal = ({ isOpen, onClose, projectData, postingType, onSuccess }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');

  const fee = PROJECT_FEES[postingType] || 0;

  const handleStripePayment = async () => {
    if (!currentUser) {
      setError('Please log in to continue with payment.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const sessionData = await createProjectCheckoutSession(
        currentUser.uid, 
        projectData, 
        postingType
      );

      if (sessionData.url) {
        // Redirect to Stripe checkout
        window.location.href = sessionData.url;
      } else if (sessionData.id) {
        // Project created directly (free posting)
        onSuccess(sessionData.id);
        onClose();
      }
    } catch (error) {
      console.error('Stripe payment error:', error);
      setError(`Payment failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalPayment = async () => {
    if (!currentUser) {
      setError('Please log in to continue with payment.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const orderData = await createProjectPayPalOrder(
        currentUser.uid, 
        projectData, 
        postingType
      );

      if (orderData.links) {
        // Find the approval URL and redirect
        const approvalUrl = orderData.links.find(link => link.rel === 'approve')?.href;
        if (approvalUrl) {
          window.location.href = approvalUrl;
        } else {
          throw new Error('PayPal approval URL not found');
        }
      } else if (orderData.id) {
        // Project created directly (free posting)
        onSuccess(orderData.id);
        onClose();
      }
    } catch (error) {
      console.error('PayPal payment error:', error);
      setError(`PayPal payment failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (fee === 0) {
      // Free posting - create project directly
      try {
        setLoading(true);
        const { createProject } = await import('../../services/firestoreService');
        const projectId = await createProject(projectData, currentUser.uid);
        onSuccess(projectId);
        onClose();
      } catch (error) {
        setError(`Failed to create project: ${error.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Paid posting
    if (paymentMethod === 'stripe') {
      await handleStripePayment();
    } else if (paymentMethod === 'paypal') {
      await handlePayPalPayment();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {fee === 0 ? 'Confirm Project Posting' : 'Project Posting Payment'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <h4 className="font-medium text-gray-900 mb-2">{projectData.title}</h4>
          <div className="text-sm text-gray-600 space-y-1">
            <p><span className="font-medium">Posting Type:</span> {postingType === 'FEATURED' ? 'Featured' : 'Basic'}</p>
            <p><span className="font-medium">Fee:</span> {fee === 0 ? 'Free' : `$${fee}`}</p>
            {postingType === 'FEATURED' && (
              <p className="text-blue-600 text-xs">
                ✨ Featured projects appear at the top of the project board and receive more visibility
              </p>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {fee > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Method
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="stripe"
                    checked={paymentMethod === 'stripe'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">Credit/Debit Card (Stripe)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="paypal"
                    checked={paymentMethod === 'paypal'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">PayPal</span>
                </label>
              </div>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Processing...' : (fee === 0 ? 'Post Project' : `Pay $${fee}`)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectPaymentModal;
